import getItemTailoredXpData from './getItemTailoredXpData';
import getErrorResponseCode from '../utils/getErrorResponseCode';
import initializeUndefinedItemProperties from '../utils/initializeUndefinedItemProperties';
import configItemResponseShapeForAmp from 'owa-mail-amp-store/lib/utils/configItemResponseShapeForAmp';
import isActionSourcePrefetch from '../utils/isActionSourcePrefetch';
import preserveItemDataInCache from '../utils/preserveItemDataInCache';
import { ActionDatapoint } from 'owa-analytics';
import { ClientItemId } from 'owa-client-ids';
import Item from 'owa-service/lib/contract/Item';
import ItemInfoResponseMessage from 'owa-service/lib/contract/ItemInfoResponseMessage';
import RequestOptions from 'owa-service/lib/RequestOptions';
import { lazyMergeSmimeDecodedMessageProperties } from 'owa-smime';
import isSmimeDecoded from 'owa-smime/lib/utils/isSmimeDecoded';
import { trace } from 'owa-trace';
import { action } from 'satcheljs/lib/legacy';
import {
    checkIfResponseSuccess,
    ClientItem,
    getBaseItemResponseShape,
    getItem,
    LoadConversationItemActionSource,
    mailStore,
    configItemResponseShapeForCLP,
} from 'owa-mail-store';
import {
    SMIME_INSTALLED_HEADER_KEY,
    SMIME_INSTALLED_HEADER_TRUE,
} from 'owa-smime/lib/utils/constants';

const assign = require('object-assign');

const ACTION_SOURCE_HEADER_KEY = 'X-OWA-ActionSource';

async function parseItemResponse(responseItem: Item, itemId: ClientItemId): Promise<void> {
    const cachedItem = mailStore.items.get(itemId.Id);
    if (cachedItem) {
        // If the item already exists in the cache, make sure we preserve any data missing on the response item
        preserveItemDataInCache(responseItem, cachedItem);
    }

    // Initialize required properties that could be undefined so they're observable in the store.
    initializeUndefinedItemProperties(responseItem);

    const newItem: ClientItem = {
        Smime: null, // Initialize Smime properties if not already present in responseItem
        TranslationData: {
            isShowingTranslation: false,
            isTranslatable: false,
            isTranslating: false,
            shouldGetFeedback: false,
            isShowingSubjectTranslation: false,
            isShowingForwardContentTranslation: false,
            isForwardContentTranslatable: false,
            manuallyTranslated: false,
            userLanguage: null,
        },
        adaptiveCardDetails: null,
        ...responseItem,
        MailboxInfo: itemId.mailboxInfo,
        SmartReplyData: null,
        SmartTimeData: null,
        SmartTaskData: null,
        SmartTimeExtendedProperty: null,
        AttachmentSuggestions: null,
        ContextualAttachmentSuggestions: null,
        smartPillFeedbackSubmitted: false,
        YammerThreadId: null,
        itemCLPInfo: null,
    };
    mailStore.items.set(itemId.Id, newItem);

    // Use the item reference from the store (http://aka.ms/mobx4)

    const mailStoreItem = mailStore.items.get(itemId.Id);
    getItemTailoredXpData(mailStoreItem);
    if (isSmimeDecoded(cachedItem)) {
        await lazyMergeSmimeDecodedMessageProperties.importAndExecute(mailStoreItem, cachedItem);
    }
}

const parseItemResponseAction = action('parseItemResponse')(parseItemResponse);

export function loadItem(
    itemIdToLoad: ClientItemId,
    actionSource: LoadConversationItemActionSource,
    topActionDatapoint?: ActionDatapoint,
    enableSmimeHeader?: boolean
): Promise<ClientItem> {
    let requestStartTime: number;
    if (topActionDatapoint) {
        requestStartTime = topActionDatapoint.addCheckmark('request_start');
    }

    // Add action source to the request header 'X-OWA-ActionSource' for diagnostic
    const options: RequestOptions = {};
    assign(options, itemIdToLoad.mailboxInfo && itemIdToLoad.mailboxInfo.requestOptions);
    const headers = options.headers ? new Headers(options.headers) : new Headers();
    if (enableSmimeHeader) {
        headers.set(SMIME_INSTALLED_HEADER_KEY, SMIME_INSTALLED_HEADER_TRUE);
    }
    if (options && options.headers) {
        headers.set(ACTION_SOURCE_HEADER_KEY, actionSource);
    }
    options.headers = headers;

    const itemResponseShape = getBaseItemResponseShape();
    configItemResponseShapeForAmp(itemResponseShape);
    configItemResponseShapeForCLP(itemResponseShape);

    return getItem(
        [itemIdToLoad.Id],
        itemResponseShape,
        'ItemNormalizedBody',
        'V2017_08_18' /* requestServerVersion */,
        options
    )
        .then(responseMessage => {
            // Add the request time as the custom data.
            if (topActionDatapoint) {
                const requestEndTime = topActionDatapoint.addCheckmark('request_end');
                topActionDatapoint.addCustomData({
                    requestTime: requestEndTime - requestStartTime,
                });
            }

            if (checkIfResponseSuccess(responseMessage[0])) {
                const response = responseMessage[0] as ItemInfoResponseMessage;
                return parseItemResponseAction(response.Items[0], itemIdToLoad).then(() =>
                    mailStore.items.get(itemIdToLoad.Id)
                );
            } else {
                switch (actionSource) {
                    case 'PrefetchFirstN':
                    case 'PrefetchSingleRow':
                    case 'PrefetchRowInCache':
                    case 'PrefetchAdjacentRowsOnDelay':
                        break;

                    case 'LoadItemReadingPane':
                    case 'LoadItemPreviewPane':
                    default:
                        trace.warn(actionSource + ': ' + responseMessage[0].MessageText);
                        break;
                }

                const [errorResponseCode, stackTrace] = getErrorResponseCode(responseMessage[0]);
                if (errorResponseCode) {
                    return Promise.reject(
                        new Error(
                            `ErrorResponseCode=${errorResponseCode}, StackTrace=${stackTrace}`
                        )
                    );
                } else {
                    return Promise.resolve(null);
                }
            }
        })
        .catch((error: Error) => {
            // Add the action source to the error message
            error.message += `, ActionSource=${actionSource}`;

            // If the action source is not prefetch, reject the error.
            if (!isActionSourcePrefetch(actionSource)) {
                return Promise.reject(error);
            } else {
                return Promise.resolve();
            }
        });
}
